import { CONFLICT_FIELDS } from '../constants'
import {
  IPatientConflictField,
  IResolvedPatientForConflict,
  IPulsaraDetails,
  IEmsChartsData,
} from '../store/pulsara.types'

type ConflictDataSource = IPulsaraDetails | IEmsChartsData

const getDisplayValue = (data: ConflictDataSource | null, key: string): string => {
  if (!data) return ''

  if (key === 'complaint') {
    return data.situation?.complaint || ''
  }

  const demo = data.patient?.patientDemographics
  if (!demo) return ''

  if (key === 'firstName' || key === 'lastName' || key === 'dOB') {
    const value = demo[key]
    const pnnv = (demo as Record<string, any>)[`${key}PNNV`]
    if (value) return value
    if (pnnv) return pnnv.name || ''
    return ''
  }

  if (key === 'gender') {
    const value = demo.gender
    const pnnv = demo.genderPNNV
    if (value) return value.name || ''
    if (pnnv) return pnnv.name || ''
    return ''
  }

  if (key === 'age') {
    const value = demo.age
    const pnnv = demo.agePNNV
    const ageUnit = demo.ageUnit
    const unit = ageUnit?.name || ''

    if (value) {
      const prefix = pnnv ? '~' : ''
      return `${prefix}${value} ${unit}`.trim()
    }
    if (pnnv) return pnnv.name || ''
    return ''
  }

  if (key === 'weight') {
    const value = demo.weight
    const pnnv = demo.weightPNNV
    const weightUnits = demo.weightUnits
    const unit = weightUnits?.name || ''

    if (value) return `${value} ${unit}`.trim()
    if (pnnv) return pnnv.name || ''
    return ''
  }

  if (key === 'height') {
    const value = demo.height
    const heightUnits = demo.heightUnits
    const unit = heightUnits?.name || ''

    if (value) return `${value} ${unit}`.trim()
    return ''
  }

  return ''
}

export const getPatientConflicts = (
  pulsaraData: IPulsaraDetails | null,
  emsChartsData: IEmsChartsData | null
): IPatientConflictField[] => {
  if (!pulsaraData) return []

  const conflicts: IPatientConflictField[] = []

  for (const field of CONFLICT_FIELDS) {
    const pulsaraValue = getDisplayValue(pulsaraData, field.key)
    const emsChartsValue = getDisplayValue(emsChartsData, field.key)

    if (!pulsaraValue && !emsChartsValue) continue

    conflicts.push({
      key: field.key,
      label: field.label,
      pulsaraValue,
      emsChartsValue,
      hasConflict: pulsaraValue !== emsChartsValue,
    })
  }

  return conflicts
}

export const calculateDefaultSelections = (
  conflictFields: IPatientConflictField[],
  FORCE_PT_SEARCH: number
): Record<string, 'pulsara' | 'emsCharts' | null> => {
  const defaultSelections: Record<string, 'pulsara' | 'emsCharts' | null> = {}

  conflictFields.forEach((field: IPatientConflictField) => {
    if (field.hasConflict) {
      if (field.emsChartsValue) {
        defaultSelections[field.key] = 'emsCharts'
      } else if (FORCE_PT_SEARCH === 1 && !field.emsChartsValue && field.pulsaraValue) {
        defaultSelections[field.key] = 'pulsara'
      } else {
        defaultSelections[field.key] = null
      }
    }
  })

  return defaultSelections
}

const FIELD_MAPPINGS: Record<string, string[]> = {
  complaint: ['complaint'],
  firstName: ['firstName', 'firstNamePNNV'],
  lastName: ['lastName', 'lastNamePNNV'],
  dOB: ['dOB', 'dOBPNNV'],
  age: ['age', 'agePNNV', 'ageUnit'],
  gender: ['gender', 'genderPNNV'],
  weight: ['weight', 'weightPNNV', 'weightUnits'],
  height: ['height', 'heightUnits'],
}

export const buildResolvedPatient = (
  pulsaraData: IPulsaraDetails | null,
  emsChartsData: IEmsChartsData | null,
  selections: Record<string, 'pulsara' | 'emsCharts' | null>
): IResolvedPatientForConflict => {
  const patientDemographics: Record<string, unknown> = {
    ...(emsChartsData?.patient?.patientDemographics ?? {}),
  }
  const situation: Record<string, unknown> = {
    ...(emsChartsData?.situation ?? {}),
  }

  for (const key of Object.keys(selections)) {
    const source = selections[key]
    const relatedFields = FIELD_MAPPINGS[key]

    if (!relatedFields) continue

    if (source === null) {
      // deselection handling by setting to default values
      for (const field of relatedFields) {
        if (key === 'complaint') {
          situation[field] = ''
        } else {
          patientDemographics[field] = field === 'firstName' || field === 'lastName' ? '' : null
        }
      }
    } else {
      const srcData = source === 'pulsara' ? pulsaraData : emsChartsData
      const demo = srcData?.patient?.patientDemographics as Record<string, unknown>

      for (const field of relatedFields) {
        if (key === 'complaint') {
          situation.complaint = srcData?.situation?.complaint ?? ''
        } else {
          patientDemographics[field] = demo?.[field]
        }
      }
    }
  }

  return { patientDemographics, situation }
}
